import { useState, useCallback } from 'react'
import { calculateDurations } from '../../../services/api/aiVideoGeneration'
import { useTaskPoller } from './useTaskPoller'

const POLL_INTERVAL = 3000 // Poll every 3 seconds for duration calculation

/**
 * Custom hook to handle video duration calculation with polling
 */
export const useCalculateDurations = ({ videoId } = {}) => {
  const [taskId, setTaskId] = useState(null)
  const [error, setError] = useState(null)

  const {
    isPolling: isCalculating,
    progress,
    status,
    message,
    startPolling,
    stopPolling
  } = useTaskPoller({ pollInterval: POLL_INTERVAL })

  const calculate = useCallback(
    async (payload, formatter, ...formatterArgs) => {
      setError(null)
      try {
        const response = await calculateDurations({
          shooting_list: payload.shooting_list,
          video_id: videoId
        })
        const id =
          response &&
          response.data &&
          (response.data.task_id ||
            response.data['task-id'] ||
            response.data.taskId)

        if (id) {
          setTaskId(id)
          const data = await startPolling(id)

          // On success, data is returned
          const output = data?.output

          console.log({ formattedOutput: formatter(output, ...formatterArgs) })

          if (formatter && typeof formatter === 'function') {
            return formatter(output, ...formatterArgs)
          }
          return output
        } else {
          throw new Error('No task_id returned from calculateDurations')
        }
      } catch (err) {
        console.error('Error in useCalculateDurations:', err)
        const errorMessage =
          err.response?.data?.message ||
          err.message ||
          'Failed to calculate durations'
        setError(errorMessage)
        throw err
      }
    },
    [startPolling, videoId]
  )

  const resetCalculation = useCallback(() => {
    setTaskId(null)
    stopPolling()
    setError(null)
  }, [stopPolling])

  return {
    taskId,
    isCalculating,
    progress,
    status,
    message,
    error: error || (status === 'failed' ? message : null),
    calculate,
    resetCalculation
  }
}

export default useCalculateDurations
